import { decodeJwt } from '@/utilities';
import Analytics from '@/mixins/Analytics';
import JwtBaseClient from './JwtBaseClient';

/**
 * @class IdentityClient
 * @description Client used to interface with Hydra Identity via the Hydra BFF.
 */
class IdentityClient {
  constructor() {
    const headers = {
      'acv-client-version': import.meta.env.VITE_VERSION,
      'acv-client-type': 'desktop',
      'acv-client-os': 'web',
      'acv-client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    this.jwtBaseClient = new JwtBaseClient(
      import.meta.env.VITE_HYDRA_API_BASE_URI,
      'response.data.error.message',
      headers,
    );
  }

  hydraLegacyLogin(email, password, reCaptchaToken = null) {
    return this.jwtBaseClient.post('api/legacylogin/v2', {
      email,
      password,
      reCaptchaResponse: reCaptchaToken,
      web: true,
    });
  }

  login(userName, password) {
    return this.jwtBaseClient.post('api/login', {
      userName,
      password,
    });
  }

  ssoLogin(ssoAuthToken) {
    return this.jwtBaseClient.post('api/sso', {
      token: ssoAuthToken,
    });
  }

  resetPassword(userName, passwordResetToken, newPassword) {
    return this.jwtBaseClient.post('api/passwordreset', {
      userName,
      passwordResetToken,
      newPassword,
    });
  }

  requestPasswordReset(userName) {
    return this.jwtBaseClient.post('api/passwordreset/request', {
      userName,
    });
  }

  refreshToken(tokens) {
    const { jwtToken, refreshToken } = tokens;
    const decodedJwtToken = decodeJwt(jwtToken) ?? {};
    const toolsId = decodedJwtToken.acvauctions_legacy_user_id ?? '';
    const identityId = decodedJwtToken.jti ?? '';
    const refreshTokenFormatRe = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-4[0-9A-Fa-f]{3}-[89AaBb][0-9A-Fa-f]{3}-[0-9A-Fa-f]{12}$/;

    if (!refreshToken) {
      try {
        Analytics.methods.noticeError(new Error('missing refresh token found'), {});
      } catch (err) {
        // do nothing
      }
    }
    if (!refreshTokenFormatRe.test(refreshToken)) {
      try {
        Analytics.methods.noticeError(new Error('malformed refresh token found'), {
          refreshToken: refreshToken === undefined ? '' : refreshToken,
          toolsId,
          identityId,
        });
      } catch (err) {
        // do nothing
      }
    }

    return this.jwtBaseClient.post('api/v2/tokenrefresh', {
      refreshTokenId: refreshToken,
    });
  }

  checkIfUserEmailExists(userExists) {
    return this.jwtBaseClient.post('api/user/exists', {
      ...userExists,
    });
  }

  addRepresentative(user, dealerId) {
    return this.jwtBaseClient.post('api/user', {
      ...user,
      organizationExternalId: dealerId,
    });
  }

  addRepresentativeSaga(user, dealerId) {
    return this.jwtBaseClient.post('api/user/saga', {
      ...user,
      organizationExternalId: dealerId,
    });
  }

  getExtendedInfoToken() {
    return this.jwtBaseClient.get('api/extendedinfotoken');
  }
}

export default IdentityClient;
